import { useState, useEffect } from 'react';
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Switch,
} from '@/components/ui';
import {
	useRequestCreatePaymentOperator,
	useRequestUpdatePaymentOperator,
} from '@/hooks/httpRequest';
import type {
	IPaymentProvider,
	PaymentProviderTypeEnum,
} from '@/interfaces/paymentProvider.interface';

import { PaymentPlatformEnum } from '@/enums/paymentPlatform.enum';

/* STRIPE is not offered here -- not supported by the API */
const VALID_PLATFORMS: PaymentPlatformEnum[] = [
	PaymentPlatformEnum.PAWAPAY,
	PaymentPlatformEnum.HUB2,
	PaymentPlatformEnum.FEDAPAY,
	PaymentPlatformEnum.PAYPAL,
	PaymentPlatformEnum.CAMPAY,
	PaymentPlatformEnum.MYCOOLPAY,
	PaymentPlatformEnum.APPLE_IAP,
	PaymentPlatformEnum.GOOGLE_IAP,
	PaymentPlatformEnum.WAVE,
	PaymentPlatformEnum.FEEXPAY,
	PaymentPlatformEnum.NEXUS,
];

const PROVIDER_TYPES: PaymentProviderTypeEnum[] = ['local', 'international'];

const parseCountries = (raw: string): string[] =>
	Array.from(
		new Set(
			raw
				.split(',')
				.map(token => token.trim().toUpperCase())
				.filter(token => token.length > 0),
		),
	);

const isValidUrl = (value: string) => /^https?:\/\/.+/.test(value.trim());

type TProps = {
	open: boolean;
	onClose: () => void;
	initialValues?: IPaymentProvider;
};

type TFormState = {
	provider: string;
	platform: PaymentPlatformEnum | '';
	type: PaymentProviderTypeEnum | '';
	logo: string;
	countries: string;
	enabled: boolean;
};

const EMPTY_FORM: TFormState = {
	provider: '',
	platform: '',
	type: '',
	logo: '',
	countries: '',
	enabled: true,
};

const OperatorFormModal = ({ open, onClose, initialValues }: TProps) => {
	const isEditMode = initialValues !== undefined;

	const { triggerCreatePaymentOperator, isLoadingCreatePaymentOperator } =
		useRequestCreatePaymentOperator();
	const { triggerUpdatePaymentOperator, isLoadingUpdatePaymentOperator } =
		useRequestUpdatePaymentOperator();

	const [form, setForm] = useState<TFormState>(EMPTY_FORM);
	const [errors, setErrors] = useState<Partial<Record<keyof TFormState, string>>>({});

	useEffect(() => {
		if (open) {
			if (initialValues) {
				setForm({
					provider: initialValues.provider,
					platform: (VALID_PLATFORMS as readonly string[]).includes(
						initialValues.platform,
					)
						? (initialValues.platform as PaymentPlatformEnum)
						: '',
					type: initialValues.type,
					logo: initialValues.logo,
					countries: initialValues.countries.join(', '),
					enabled: initialValues.enabled !== false,
				});
			} else {
				setForm(EMPTY_FORM);
			}
			setErrors({});
		}
	}, [open, initialValues]);

	const validate = (): boolean => {
		const nextErrors: Partial<Record<keyof TFormState, string>> = {};

		if (!isEditMode && !form.provider.trim()) {
			nextErrors.provider = 'Champ requis';
		}
		if (!form.platform) {
			nextErrors.platform = 'Champ requis';
		}
		if (!form.type) {
			nextErrors.type = 'Champ requis';
		}
		if (!form.logo.trim()) {
			nextErrors.logo = 'Champ requis';
		} else if (!isValidUrl(form.logo)) {
			nextErrors.logo = 'URL invalide (doit commencer par http:// ou https://)';
		}
		const parsedCountries = parseCountries(form.countries);
		if (parsedCountries.length === 0) {
			nextErrors.countries = 'Au moins un pays est requis';
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validate()) return;

		const parsedCountries = parseCountries(form.countries);

		try {
			if (isEditMode) {
				await triggerUpdatePaymentOperator({
					operatorKey: initialValues.provider,
					dto: {
						platform: form.platform as PaymentPlatformEnum,
						type: form.type as PaymentProviderTypeEnum,
						logo: form.logo.trim(),
						countries: parsedCountries,
						enabled: form.enabled,
					},
				});
			} else {
				await triggerCreatePaymentOperator({
					provider: form.provider.trim(),
					platform: form.platform as PaymentPlatformEnum,
					type: form.type as PaymentProviderTypeEnum,
					logo: form.logo.trim(),
					countries: parsedCountries,
					enabled: form.enabled,
				});
			}
			onClose();
		} catch {
			/* error already surfaced via notify() in the hook */
		}
	};

	const isSubmitting = isLoadingCreatePaymentOperator || isLoadingUpdatePaymentOperator;

	const field = (key: keyof TFormState, label: string, children: React.ReactNode) => (
		<div className="flex flex-col gap-1.5">
			<Label>{label}</Label>
			{children}
			{errors[key] && <p className="text-xs text-destructive">{errors[key]}</p>}
		</div>
	);

	return (
		<Dialog
			open={open}
			onOpenChange={open => {
				if (!open && !isSubmitting) onClose();
			}}
		>
			<DialogContent className="sm:max-w-[480px] duration-0 animate-none">
				<DialogHeader>
					<DialogTitle>
						{isEditMode ? `Modifier ${initialValues.provider}` : 'Ajouter un opérateur'}
					</DialogTitle>
				</DialogHeader>

				<div className="flex flex-col gap-4 py-2">
					{field(
						'provider',
						'Identifiant opérateur',
						<Input
							value={form.provider}
							onChange={e => setForm(prev => ({ ...prev, provider: e.target.value }))}
							placeholder="ex: WAVE_SN"
							disabled={isEditMode || isSubmitting}
						/>,
					)}

					{field(
						'platform',
						'Plateforme',
						<Select
							value={form.platform}
							onValueChange={value =>
								setForm(prev => ({ ...prev, platform: value as PaymentPlatformEnum }))
							}
							disabled={isSubmitting}
						>
							<SelectTrigger>
								<SelectValue placeholder="Sélectionner une plateforme" />
							</SelectTrigger>
							<SelectContent>
								{VALID_PLATFORMS.map(platform => (
									<SelectItem key={platform} value={platform}>
										{platform}
									</SelectItem>
								))}
							</SelectContent>
						</Select>,
					)}

					{field(
						'type',
						'Type',
						<Select
							value={form.type}
							onValueChange={value =>
								setForm(prev => ({ ...prev, type: value as PaymentProviderTypeEnum }))
							}
							disabled={isSubmitting}
						>
							<SelectTrigger>
								<SelectValue placeholder="Sélectionner un type" />
							</SelectTrigger>
							<SelectContent>
								{PROVIDER_TYPES.map(type => (
									<SelectItem key={type} value={type}>
										{type}
									</SelectItem>
								))}
							</SelectContent>
						</Select>,
					)}

					{field(
						'logo',
						'URL du logo',
						<Input
							value={form.logo}
							onChange={e => setForm(prev => ({ ...prev, logo: e.target.value }))}
							placeholder="https://..."
							disabled={isSubmitting}
						/>,
					)}

					{field(
						'countries',
						'Pays (séparés par des virgules)',
						<Input
							value={form.countries}
							onChange={e => setForm(prev => ({ ...prev, countries: e.target.value }))}
							placeholder="ex: SN, CI, AFRICA"
							disabled={isSubmitting}
						/>,
					)}

					<div className="flex items-center gap-3">
						<Switch
							checked={form.enabled}
							onCheckedChange={checked =>
								setForm(prev => ({ ...prev, enabled: checked }))
							}
							disabled={isSubmitting}
						/>
						<Label>Activé</Label>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={isSubmitting}>
						Annuler
					</Button>
					<Button onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? 'Enregistrement...' : isEditMode ? 'Modifier' : 'Ajouter'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default OperatorFormModal;
